import { Box, Button, Flex } from '@chakra-ui/react';
import { pathDict } from '@ui/config/paths';
import useBoostMultiplier from '@ui/hooks/useBoostMultiplier';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { useNumberOfStakedNfts } from '@ui/hooks/useCruisers';
import useGardenApy from '@ui/hooks/useGardenApy';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import useLpAmountsStaked from '@ui/hooks/useLpAmountsStaked';
import usePendingVno from '@ui/hooks/usePendingVno';
import { useVnoUsdValue } from '@ui/hooks/useVnoUsdValue';
import { useRouter } from '@ui/overrides/next/router';
import { formatTokenAmount } from '@ui/utils/format';
import type { GardenType } from '@ui/utils/garden';
import { formatGardenApy, gardenTokenDict } from '@ui/utils/garden';
import GardenNftOverview from '@ui/views/Garden/GardenNftOverview';
import React from 'react';

import { features } from '../Features';
import Info from '../Info';
import { LPTokenIcon } from '../LPTokenIcon';

type GardenInfoSlideProps = {
  gardenType: GardenType;
};

const cruiserStakingEnabled = isFeatureEnabled(features.NftStaking);

/**
 * this component will be only used in the CRO coin mode
 * @deprecated todo cover.lv remove this code after the atom released
 */
function GardenInfoSlide({ gardenType }: GardenInfoSlideProps) {
  const coinMode: CoinMode = 'cro';
  const { staked, apy } = useGardenInfoSlide(gardenType);
  const { data: boostMultiplier } = useBoostMultiplier(gardenType);
  const { pendingVno } = usePendingVno(gardenType);
  const { data: pendingVnoUsd, isLoading: pendingVnoUsdLoading } =
    useVnoUsdValue(pendingVno);
  const router = useRouter();
  const { data: numberOfStakedCruisers } = useNumberOfStakedNfts(gardenType);

  return (
    <>
      <Flex gap={4} flexDir="column">
        <Info
          title="Planted"
          value={
            staked ? (
              <LPTokenIcon coinMode={coinMode} gardenType={gardenType}>
                {formatTokenAmount(
                  staked,
                  4,
                  gardenTokenDict[coinMode][gardenType].decimals,
                )}
              </LPTokenIcon>
            ) : undefined
          }
        />
        <Info
          title="APR"
          value={
            apy !== undefined ? (
              <Box
                color={
                  (boostMultiplier ?? 0) > 1
                    ? 'garden.highlight'
                    : 'text.normal'
                }
              >
                {apy}
              </Box>
            ) : undefined
          }
        />
        {cruiserStakingEnabled && (
          <Info
            title="Helpers employed"
            value={
              numberOfStakedCruisers !== undefined && (
                <>
                  <Box>{numberOfStakedCruisers}</Box>
                  <GardenNftOverview gardenType={gardenType} />
                </>
              )
            }
          />
        )}
        <Info
          title="Total active boost"
          value={boostMultiplier ? boostMultiplier.toFixed(2) + 'x' : undefined}
        />
        <Info
          title="Rewards"
          value={
            pendingVno ? formatTokenAmount(pendingVno) + ' VNO' : undefined
          }
          convertedValue={
            pendingVnoUsd
              ? formatTokenAmount(pendingVnoUsd) + ' USD'
              : undefined
          }
          convertedValueLoading={pendingVnoUsdLoading}
        />
      </Flex>
      <Button
        hidden={!pendingVno || pendingVno.lte(0)}
        mt={3}
        variant="primary"
        onClick={() => {
          router.push(pathDict[coinMode].garden[gardenType].HARVEST);
        }}
      >
        Harvest rewards
      </Button>
    </>
  );
}

const useGardenInfoSlide = (gardenType: GardenType) => {
  const { stakedFerro, stakedTectonic, stakedVvs, stakedVvsCro } =
    useLpAmountsStaked(gardenType);
  const { vvsGardenApy, ferroGardenApy, tectonicGardenApy, vvsCroGardenApy } =
    useGardenApy(gardenType);

  let staked = stakedFerro;
  let apy = ferroGardenApy;
  switch (gardenType) {
    case 'TECTONIC':
      staked = stakedTectonic;
      apy = tectonicGardenApy;
      break;
    case 'VVS':
      staked = stakedVvs;
      apy = vvsGardenApy;
      break;
    case 'VVS_CRO':
      staked = stakedVvsCro;
      apy = vvsCroGardenApy;
      break;
  }

  return {
    staked,
    apy: formatGardenApy(apy),
  };
};

export default GardenInfoSlide;
